import { memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Eye, Edit, Trash2, TruckIcon, PackageCheck } from "lucide-react";
import { RentalStatus, RentalType } from "@/shared/types/enums";
import {
  formatCurrency,
  formatDate,
  formatPhone,
} from "@/shared/lib/formatters";
import type { RentalResponseDTO } from "@/shared/types/rental.types";
import type { TagResponseDTO } from "@/features/tags/types";
import { RentalStatusBadge } from "../../components/RentalStatusBadge";

interface RentalsTableProps {
  rentals: RentalResponseDTO[];
  loading: boolean;
  page: number;
  totalPages: number;
  tagsMap: Map<number, TagResponseDTO>;
  loadingId: number | null;
  onView: (rental: RentalResponseDTO) => void;
  onEdit: (rental: RentalResponseDTO) => void;
  onDelete: (rental: RentalResponseDTO) => void;
  onPickup: (rental: RentalResponseDTO) => void;
  onReturn: (rental: RentalResponseDTO) => void;
  onPageChange: (page: number) => void;
}

export const RentalsTable = memo(function RentalsTable({
  rentals,
  loading,
  page,
  totalPages,
  tagsMap,
  loadingId,
  onView,
  onEdit,
  onDelete,
  onPickup,
  onReturn,
  onPageChange,
}: RentalsTableProps) {
  const canEdit = (r: RentalResponseDTO) =>
    r.status !== RentalStatus.FINISHED && r.status !== RentalStatus.CANCELED;
  const canDelete = (r: RentalResponseDTO) =>
    r.status === RentalStatus.INCOMPLETE || r.status === RentalStatus.COMPLETE;
  const canPickup = (r: RentalResponseDTO) =>
    r.status === RentalStatus.COMPLETE;
  const canReturn = (r: RentalResponseDTO) =>
    r.status === RentalStatus.ON_STREET;

  return (
    <div className="space-y-3">
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contrato</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Período</TableHead>
              <TableHead>Vendedor</TableHead>
              <TableHead>Tag</TableHead>
              <TableHead>Itens</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rentals.length === 0 && !loading && (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-10 text-muted-foreground"
                >
                  Nenhum aluguel encontrado
                </TableCell>
              </TableRow>
            )}
            {rentals.map((r) => {
              const firstTag = r.tagIds[0] ? tagsMap.get(r.tagIds[0]) : null;
              const itemsLabel =
                r.rentalType === RentalType.GOWN
                  ? `${r.gownItems?.reduce((acc, g) => acc + g.quantity, 0) ?? 0} beca(s)`
                  : `${r.stockItems?.length ?? 0} peça(s)${r.hasShoes ? ` + ${r.shoesPairQuantity} par(es) sapato` : ""}`;
              const isRowLoading = loadingId === r.id;

              return (
                <TableRow
                  key={r.id}
                  className={isRowLoading ? "opacity-50" : ""}
                >
                  <TableCell className="font-medium">
                    {r.contractNumber}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{r.clientName}</div>
                    {r.clientPhone && (
                      <div className="text-xs text-muted-foreground">
                        {formatPhone(r.clientPhone)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    <div>{formatDate(r.expectedOutAt)}</div>
                    <div className="text-muted-foreground">
                      → {formatDate(r.expectedInAt)}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{r.sellerName}</TableCell>
                  <TableCell>
                    {firstTag ? (
                      <div className="flex items-center gap-1">
                        <Badge
                          variant="outline"
                          style={{
                            borderColor: firstTag.color,
                            color: firstTag.color,
                          }}
                        >
                          {firstTag.name}
                        </Badge>
                        {r.tagIds.length > 1 && (
                          <Badge
                            variant="outline"
                            className="text-muted-foreground text-xs"
                          >
                            +{r.tagIds.length - 1}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {itemsLabel}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">
                      {formatCurrency(r.totalValue)}
                    </div>
                    {r.paidValue > 0 && (
                      <div className="text-xs text-green-500">
                        {formatCurrency(r.paidValue)} pago
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <RentalStatusBadge
                      status={r.status}
                      expectedInAt={r.expectedInAt}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => onView(r)}
                        aria-label="Visualizar detalhes"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => onEdit(r)}
                        disabled={!canEdit(r) || isRowLoading}
                        aria-label="Editar aluguel"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDelete(r)}
                        disabled={!canDelete(r) || isRowLoading}
                        aria-label="Cancelar aluguel"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-blue-500 hover:text-blue-400"
                        onClick={() => onPickup(r)}
                        disabled={!canPickup(r) || isRowLoading}
                        aria-label="Registrar retirada"
                      >
                        <TruckIcon className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-green-500 hover:text-green-400"
                        onClick={() => onReturn(r)}
                        disabled={!canReturn(r) || isRowLoading}
                        aria-label="Registrar devolução"
                      >
                        <PackageCheck className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
          >
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
});
